import {InfoPopover, RadioGroup, UnderlinedHeader} from "../../components";
import React from "react";
import {Switch} from "../../components/DisablableSwitch/Switch";
import {NonLifestyleGoalType} from "../models/GoalType";
import {useAnalyticsTrackers} from "../../utils/useAnalyticsTrackers";
import {useAppSelector} from "../../store/hooks";
import {selectReleaseToggles} from "../../ReleaseToggles/releaseTogglesSlice";

interface GoalFundingProps {
    nonLifestyleGoal: NonLifestyleGoalType,
    updateNonLifestyleGoal: (updatedNonLifestyleGoal: Partial<NonLifestyleGoalType['userInputs']>) => void,
    isProfileWithProposalsOrArchived?: boolean
}

export const GoalFunding = ({
                                nonLifestyleGoal,
                                updateNonLifestyleGoal,
                                isProfileWithProposalsOrArchived = false
                            }: GoalFundingProps) => {

    const {page} = useAnalyticsTrackers();
    const enableEnhancedLifestyleFunding = useAppSelector(selectReleaseToggles)?.enableEnhancedLifestyleFunding;

    const investableRadioOption = "Funded by Investable Assets (Current Portfolio)";
    const nonInvestableRadioOption = "Funded by Non-Investable Assets (Inflows, Illiquid Assets, Personal Assets)";

    function booleanToRadioButton(value: boolean): string {
        switch (value) {
            case true: return nonInvestableRadioOption;
            case false: return investableRadioOption;
            default: return investableRadioOption;
        }
    }

    function radioButtonToBoolean(value: string) {
        return value === nonInvestableRadioOption;
    }

    const isOn = !!nonLifestyleGoal.userInputs.isFundedByNonInvestableAssets;

    const trackAndUpdate = (nextIsOn: boolean) => {
        if (nextIsOn) {
            page(`${nonLifestyleGoal.goalType}_Funded_By_Non_Investable_Assets`);
        } else {
            page(`${nonLifestyleGoal.goalType}_Funded_By_Investable_Assets`);
        }
        updateNonLifestyleGoal({isFundedByNonInvestableAssets: nextIsOn});
    };

    const handleToggle = () => {
        if (isProfileWithProposalsOrArchived) return;
        trackAndUpdate(!isOn);
    };

    const header = (
        <UnderlinedHeader
            primaryText='Goal Funding'
            secondaryContent={
                <InfoPopover
                    content={<div>Indicates whether the proposed allocation of the goal will
                        impact the proposed asset allocation of the client's portfolio. Visit the Goal Funding
                        page to view and edit this information for all goals to confirm they are aligned to
                        the proper assets and avoid a shortfall of goal-aligned investable assets.</div>}
                    direction="top"
                    width="288px"
                    label="goal-funding-popover"
                    arrow
                />
            }
            className="goal-timeframe-underlined-header"
        />
    );

    if (!enableEnhancedLifestyleFunding) {
        return <section aria-label="Goal Funding">
            {header}
            <RadioGroup
                name={""}
                data-testid={"goal-funding-radio-group"}
                layout="vertical"
                values={[investableRadioOption, nonInvestableRadioOption]}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const newValue = e.target.value;
                    trackAndUpdate(radioButtonToBoolean(newValue));
                }}
                selected={booleanToRadioButton(nonLifestyleGoal.userInputs.isFundedByNonInvestableAssets)}
                disabled={isProfileWithProposalsOrArchived}
            />
        </section>;
    }

    return <section aria-label="Goal Funding" className="lifestyle-spending-section non-lifestyle-goal-funding">
        {header}

        <div className="lifestyle-goal-funding-table">
            <label htmlFor="fund-using-non-investable-assets-switch">
                <b>Fund using <br/><span style={{whiteSpace: 'nowrap'}}>Non-Investable Assets</span></b>
            </label>
            <span className="funded-by-non-investable-assets-slider-container">
                <span
                    className="funded-by-non-investable-assets-slider"
                    data-testid="funded-by-non-investable-assets-slider"
                >
                    <Switch
                        id="fund-using-non-investable-assets-switch"
                        isOn={isOn}
                        offText="OFF"
                        onText="ON"
                        stateTextPosition="right"
                        isDisabled={isProfileWithProposalsOrArchived}
                        FBNIToggle={handleToggle}
                    />
                </span>
            </span>
            <span />
            <span className="paddingright-xl non-lifestyle-goal-funding__subtext" style={{fontSize: "13px"}}>
                E.g., Illiquid Assets and Personal Assets
            </span>
        </div>
    </section>
}
